import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom'
import { formatInTimeZone } from 'date-fns-tz'
import { api } from '../services/api'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/common/LoadingSpinner'
import ErrorMessage from '../components/common/ErrorMessage'
import ConfirmationModal from '../components/common/ConfirmationModal'
import { theme, hslToHex } from '../config/theme'
import EventHeader from '../components/events/EventHeader'
import EventSearch from '../components/events/EventSearch'
import EventList from '../components/events/EventList'
import { trackEngagement } from '../utils/analytics'
import { usePageTitle } from '../hooks/usePageTitle'

// Calculate distance between two points using the Haversine formula
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3958.8 // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  const distance = R * c
  return Math.round(distance * 10) / 10 // Round to 1 decimal place
}

export default function EventsPage() {
  const { searchTerm, radius: urlRadius, sortBy: urlSortBy } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  usePageTitle(searchTerm ? `Garage Sales near ${decodeURIComponent(searchTerm)}` : 'Garage Sale Events')
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [searchAddress, setSearchAddress] = useState('')
  const [searchRadius, setSearchRadius] = useState(urlRadius ? parseInt(urlRadius) : 25)
  const [searchCoordinates, setSearchCoordinates] = useState(null)
  const [isSearching, setIsSearching] = useState(false)
  const [sortBy, setSortBy] = useState(urlSortBy || 'soonest')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [userEventIds, setUserEventIds] = useState([])
  const [hasSearched, setHasSearched] = useState(false)
  const suggestionsRef = useRef(null)
  const { user } = useAuth()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [eventToDelete, setEventToDelete] = useState(null)
  const observer = useRef()
  const eventsApi = useApi()
  const [page, setPage] = useState(1)
  const lastEventElementRef = useRef()
  const loadingRef = useRef(false)

  // Handle URL search parameter changes (radius/sortBy)
  useEffect(() => {
    if (searchTerm) {
      const decodedSearchTerm = decodeURIComponent(searchTerm)
      setSearchAddress(decodedSearchTerm)
      handleSearchWithTerm(decodedSearchTerm)
    }
  }, [searchTerm, urlRadius, urlSortBy])

  const handleSearchWithTerm = async (term) => {
    try {
      setIsSearching(true)
      setError(null)

      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          term
        )}.json?access_token=${import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}&types=address&limit=1`
      )
      const data = await response.json()

      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center
        setSearchCoordinates({ latitude: lat, longitude: lng })
        setCurrentPage(1)
        loadEvents(1, { latitude: lat, longitude: lng }, true)
        
        // Track search event
        trackEngagement.searchPerformed('location', term)
      } else {
        setError('No results found for this location.')
        setEvents([])
      }
    } catch (error) {
      console.error('Error searching address:', error)
      setError('Unable to search location. Please try again.')
    } finally {
      setIsSearching(false)
    }
  }

  const handleSearch = async (e) => {
    e.preventDefault()
    if (!searchAddress.trim()) return

    setShowSuggestions(false)
    setSuggestions([])
    
    // Track search event
    trackEngagement.searchPerformed('location', searchAddress)
    
    // Update URL with search parameters
    const encodedSearch = encodeURIComponent(searchAddress)
    navigate(`/search/${encodedSearch}/${searchRadius}/${sortBy}`)
  }

  // Load events on mount: search term triggers geocode search, otherwise load recent events
  useEffect(() => {
    if (searchTerm) {
      const decodedSearchTerm = decodeURIComponent(searchTerm)
      setSearchAddress(decodedSearchTerm)
      setHasSearched(true)
      handleSearchWithTerm(decodedSearchTerm)
    } else {
      // No search term: load recently created events immediately
      loadEvents(1, null, true)

      // Also try to get user's location for future searches
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            try {
              const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}&types=address&limit=1`
              );
              const data = await response.json();
              if (data.features && data.features.length > 0) {
                setSearchAddress(data.features[0].place_name);
              }
            } catch (error) {
              console.error('Error getting address from coordinates:', error);
            }
          },
          (error) => {
            console.error('Error getting location:', error);
          }
        );
      }
    }
  }, [searchTerm]);

  const distanceOptions = [
    { value: 5, label: '5 miles' },
    { value: 10, label: '10 miles' },
    { value: 25, label: '25 miles' },
    { value: 50, label: '50 miles' },
    { value: 100, label: '100 miles' },
    { value: 1000, label: '>100 miles' }
  ]

  const sortOptions = [
    { value: 'soonest', label: 'Soonest' },
    { value: 'closest', label: 'Closest' }
  ]

  const handleAddressInput = async (e) => {
    const value = e.target.value
    setSearchAddress(value)
    
    if (value.length < 3) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          value
        )}.json?access_token=${import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}&types=address&limit=5`
      )
      const data = await response.json()
      
      if (data.features) {
        setSuggestions(data.features)
        setShowSuggestions(true)
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error)
    }
  }

  const handleSuggestionClick = (suggestion) => {
    if (!suggestion) {
      setShowSuggestions(false)
      return
    }

    const [lng, lat] = suggestion.center
    setSearchAddress(suggestion.place_name)
    setSearchCoordinates({ latitude: lat, longitude: lng })
    setSuggestions([])
    setShowSuggestions(false)
    setCurrentPage(1)
    loadEvents(1, { latitude: lat, longitude: lng }, true)
  }

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleRadiusChange = (radius) => {
    setSearchRadius(radius)
    // Update URL with new radius
    if (searchTerm) {
      const encodedSearch = encodeURIComponent(searchTerm)
      navigate(`/search/${encodedSearch}/${radius}/${sortBy}`)
    }
  }

  const handleSortChange = (sortValue) => {
    setSortBy(sortValue)
    // Update URL with new sort value
    if (searchTerm) {
      const encodedSearch = encodeURIComponent(searchTerm)
      navigate(`/search/${encodedSearch}/${searchRadius}/${sortValue}`)
    }
  }

  const sortEvents = (events) => {
    // Ensure events is always an array
    if (!events || !Array.isArray(events)) {
      return []
    }

    const safeEvents = Array.isArray(events) ? events : []

    // Add distance and isUserEvent properties
    const eventsWithMeta = safeEvents.map(event => {
      const isUserEvent = userEventIds.includes(event.id)
      if (searchCoordinates && event.location?.latitude && event.location?.longitude) {
        const distance = calculateDistance(
          searchCoordinates.latitude,
          searchCoordinates.longitude,
          parseFloat(event.location.latitude),
          parseFloat(event.location.longitude)
        )
        return { ...event, distance, isUserEvent }
      }
      return { ...event, distance: null, isUserEvent }
    })

    // Sort: user's events first, then by selected sort criteria
    return eventsWithMeta.sort((a, b) => {
      // User's events always come first
      if (a.isUserEvent && !b.isUserEvent) return -1
      if (!a.isUserEvent && b.isUserEvent) return 1

      if (sortBy === 'soonest') {
        return new Date(a.start_date) - new Date(b.start_date)
      } else if (sortBy === 'closest' && searchCoordinates) {
        const distanceA = a.distance
        const distanceB = b.distance

        if (distanceA === null && distanceB === null) return 0
        if (distanceA === null) return 1
        if (distanceB === null) return -1

        return distanceA - distanceB
      }
      return 0
    })
  }

  const loadEvents = useCallback(async (pageNum = 1, coordinates = null, reset = false) => {
    if (loadingRef.current) {
            return
    }

    loadingRef.current = true
    setLoading(true)
    try {
      const params = { page: pageNum }
      if (coordinates) {
        params.latitude = coordinates.latitude
        params.longitude = coordinates.longitude
        params.radius = searchRadius
        params.sort_by = sortBy
        setHasSearched(true)
      }

      const response = await api.events.getAll({ params })

      // Capture user's event IDs from meta
      if (response?.meta?.user_event_ids) {
        setUserEventIds(response.meta.user_event_ids)
      }

      // Ensure response.events is always an array
      let newEvents = []
      if (response && response.events) {
        newEvents = Array.isArray(response.events) ? response.events : []
      }

      if (reset || pageNum === 1) {
        setEvents(newEvents)
      } else {
        setEvents(prev => {
          // Ensure prev is always an array
          const safePrev = Array.isArray(prev) ? prev : []
          return [...safePrev, ...newEvents]
        })
      }

      setHasMore(newEvents.length === 12) // Backend returns 12 per page
      setCurrentPage(pageNum)
    } catch (error) {
      console.error('Error loading events:', error)
      // Set events to empty array on error to prevent undefined issues
      if (reset || pageNum === 1) {
        setEvents([])
      }
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [searchRadius, sortBy])

  useEffect(() => {
    if (searchCoordinates) {
      setCurrentPage(1)
      loadEvents(1, searchCoordinates, true)
    }
  }, [searchCoordinates, searchRadius, sortBy])

  const handleEventDeleted = (eventId) => {
    setEvents(prev => prev.filter(event => event.id !== eventId))
  }

  const lastEventElementCallback = useCallback(node => {
    if (loadingRef.current) return
    if (lastEventElementRef.current) lastEventElementRef.current.disconnect()
    lastEventElementRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
                // Add small delay to prevent rapid-fire calls
        setTimeout(() => {
          if (!loadingRef.current && hasMore) {
            loadEvents(currentPage + 1, searchCoordinates, false)
          }
        }, 100)
      }
    }, {
      threshold: 0.1,
      rootMargin: '100px'
    })
    if (node) lastEventElementRef.current.observe(node)
  }, [hasMore, currentPage, searchCoordinates, loadEvents])

  const formatDateInTimezone = (dateString, timezone) => {
    return formatInTimeZone(new Date(dateString), timezone, 'MMM d, yyyy')
  }

  const formatDateRange = (startDate, endDate, timezone) => {
    const start = formatDateInTimezone(startDate, timezone)
    const end = formatDateInTimezone(endDate, timezone)
    return start === end ? start : `${start} - ${end}`
  }

  const isEventCurrent = (event) => {
    const now = new Date()
    const startDate = new Date(event.start_date)
    const endDate = new Date(event.end_date)
    return startDate <= now && endDate >= now
  }

  const getMapImageUrl = (location) => {
    if (!location?.latitude || !location?.longitude) return null
    const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
    if (!mapboxToken) return null

    const width = 800
    const height = 400
    const zoom = 14
    const markerColor = hslToHex(theme.colors.primary[500])
    const marker = `pin-s+${markerColor}(${location.longitude},${location.latitude})`
    
    return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${marker}/${location.longitude},${location.latitude},${zoom},0/${width}x${height}?access_token=${mapboxToken}`
  }

  const resetToCurrentLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          try {
            // Reverse geocode the coordinates to get the address
            const response = await fetch(
              `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}&types=address&limit=1`
            );
            const data = await response.json();
            
            if (data.features && data.features.length > 0) {
              const address = data.features[0].place_name;
              setSearchAddress(address);
              setSearchCoordinates({ latitude, longitude });
              setCurrentPage(1);
              loadEvents(1, { latitude, longitude }, true);
              
              // Update URL with current location
              const encodedSearch = encodeURIComponent(address);
              navigate(`/search/${encodedSearch}/${searchRadius}/${sortBy}`);
            }
          } catch (error) {
            console.error('Error getting address from coordinates:', error);
          }
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  }

  const getHeroText = () => {
    if (!searchTerm) return 'Find Local Garage Sales'
    
    const decodedSearch = decodeURIComponent(searchTerm)
    if (location.pathname.startsWith('/cities/')) {
      return `Local Garage Sales in ${decodedSearch}`
    }
    return `Local Garage Sales near ${decodedSearch}`
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <EventHeader searchTerm={searchTerm} user={user} />
        <ErrorMessage
          message={error}
          className="mb-8"
        />
      </div>
    )
  }

  return (
    <div className="bg-bg">
      {/* Hero Section — full width */}
      <div className="relative">
        <div className="absolute inset-0">
          <picture>
            <source
              type="image/avif"
              srcSet="/images/hero-events-mobile.avif 960w, /images/hero-events.avif 1920w"
              sizes="100vw"
            />
            <img
              src="/images/hero-events-fallback.jpg"
              alt="Community garage sale event"
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl drop-shadow-lg">
            {getHeroText()}
          </h2>
          <p className="mt-6 max-w-lg text-xl text-white/90 sm:max-w-3xl drop-shadow-lg">
            Discover the best garage sales in your area. Search by location and find great deals near you.
          </p>
          <div className="mt-8">
            <Link
              to={user ? "/events/new" : "/login"}
              className="btn-primary min-h-[48px] px-6 text-base"
            >
              Submit an Event
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <EventSearch
        searchAddress={searchAddress}
        searchRadius={searchRadius}
        sortBy={sortBy}
        suggestions={suggestions}
        showSuggestions={showSuggestions}
        isSearching={isSearching}
        onSearch={handleSearch}
        onAddressInput={handleAddressInput}
        onSuggestionClick={handleSuggestionClick}
        onRadiusChange={handleRadiusChange}
        onSortChange={handleSortChange}
        onResetLocation={resetToCurrentLocation}
        distanceOptions={distanceOptions}
        sortOptions={sortOptions}
      />

      {events.length === 0 && !loading ? (
        <div className="empty-state">
          <p className="empty-state-title">{hasSearched ? 'No events found.' : 'No upcoming events yet.'}</p>
          <p className="empty-state-description mb-4">
            {hasSearched
              ? 'Be the first to create a garage sale event in this area.'
              : 'Search for events near you, or create the first one!'}
          </p>
          <Link to="/events/new" className="btn-primary">
            Create the first event
          </Link>
        </div>
      ) : (
        <EventList
          events={sortEvents(events)}
          user={user}
          searchCoordinates={searchCoordinates}
          loading={loading}
          hasMore={hasMore}
          onEventDeleted={handleEventDeleted}
          isLastSection={true}
          lastEventElementRef={lastEventElementCallback}
        />
      )}
      </div>
    </div>
  )
}